/* Thin fetch wrapper around the Procurement API.
 *
 * Mirrors the ecosystem auth model: a short-lived JWT access token sent as a
 * Bearer header, plus a refresh token used to silently mint a new access token
 * on 401. Tokens live in localStorage so a reload keeps the session.
 */

import { buildErrorMessage } from './apiErrors';
import { t } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5080/api';

const STORAGE_KEY = 'pp.auth';
const PROPERTY_KEY = 'pp.activePropertyId';

export function getStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStored(auth) {
  if (auth) localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  else localStorage.removeItem(STORAGE_KEY);
}

export function getActivePropertyId() {
  return localStorage.getItem(PROPERTY_KEY) || null;
}

export function setActivePropertyId(propertyId) {
  if (propertyId) localStorage.setItem(PROPERTY_KEY, propertyId);
  else localStorage.removeItem(PROPERTY_KEY);
}

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function parse(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

let refreshInFlight = null;

async function doRefresh() {
  const stored = getStored();
  if (!stored?.refreshToken) throw new ApiError('No refresh token', 401);

  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: stored.refreshToken }),
    })
      .then(async (res) => {
        if (!res.ok) throw new ApiError('Refresh failed', res.status);
        const data = await parse(res);
        setStored(data);
        return data;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * fetch() waits forever by default, so a stalled request left pages sitting on a spinner with no way
 * out. Aborting converts that into an error the page can render and offer a retry from. File
 * transfers get a longer budget because they are legitimately slow on hotel wifi.
 */
const REQUEST_TIMEOUT_MS = 45000;
const UPLOAD_TIMEOUT_MS = 180000;

/**
 * Core request. Attaches auth + active property headers, parses JSON, and
 * transparently retries once after refreshing an expired access token.
 */
export async function apiFetch(path, { method = 'GET', body, headers = {}, auth = true, timeoutMs, _retry = false } = {}) {
  const stored = getStored();
  const finalHeaders = { Accept: 'application/json', ...headers };

  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (auth && stored?.accessToken) {
    finalHeaders.Authorization = `Bearer ${stored.accessToken}`;
  }
  const propertyId = getActivePropertyId();
  if (propertyId) finalHeaders['X-Property-Id'] = propertyId;

  const budget = timeoutMs ?? (body instanceof FormData ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), budget);

  let res;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      headers: finalHeaders,
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new ApiError(t('The server took too long to respond. Please try again.'), 408);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 401 && auth && !_retry && stored?.refreshToken) {
    try {
      await doRefresh();
      return apiFetch(path, { method, body, headers, auth, timeoutMs, _retry: true });
    } catch {
      setStored(null);
      throw new ApiError('Session expired', 401);
    }
  }

  if (!res.ok) {
    const errBody = await parse(res);
    throw new ApiError(buildErrorMessage(errBody, res.status), res.status, errBody);
  }

  return parse(res);
}

export const api = {
  get: (path, opts) => apiFetch(path, { ...opts, method: 'GET' }),
  post: (path, body, opts) => apiFetch(path, { ...opts, method: 'POST', body }),
  put: (path, body, opts) => apiFetch(path, { ...opts, method: 'PUT', body }),
  /**
   * PUT for resources whose update command repeats the id in its body. Stamping it here means a
   * caller cannot forget it: omitting it made the API reject the request during model binding,
   * long before any real validation ran, and the user only saw "one or more validation errors".
   */
  putWithId: (path, id, body, opts) => apiFetch(path, { ...opts, method: 'PUT', body: { ...body, id } }),
  patch: (path, body, opts) => apiFetch(path, { ...opts, method: 'PATCH', body }),
  del: (path, opts) => apiFetch(path, { ...opts, method: 'DELETE' }),
};

export const auth = {
  login: (email, password) => apiFetch('/auth/login', { method: 'POST', body: { email, password }, auth: false }),
  forgotPassword: (email) =>
    apiFetch('/auth/forgot-password', { method: 'POST', body: { email }, auth: false }),
  resetPassword: (token, newPassword) =>
    apiFetch('/auth/reset-password', { method: 'POST', body: { token, newPassword }, auth: false }),
  me: () => apiFetch('/auth/me'),
  logout: (refreshToken) =>
    apiFetch('/auth/logout', { method: 'POST', body: { refreshToken }, auth: false }).catch(() => null),
  updateProfile: (payload) => apiFetch('/me/profile', { method: 'PUT', body: payload }),
  updateSignature: (signatureImage) => apiFetch('/me/signature', { method: 'PUT', body: { signatureImage } }),
  completeOnboarding: (language) => apiFetch('/me/onboarding', { method: 'PUT', body: { language } }),
  updateNotifications: (approvalEmailsOptOut, commentEmailsOptOut) =>
    apiFetch('/me/notifications', { method: 'PUT', body: { approvalEmailsOptOut, commentEmailsOptOut } }),
  // Both return a fresh token pair (other sessions are signed out server-side);
  // the caller must swap it into storage via setStored.
  changePassword: (currentPassword, newPassword) =>
    apiFetch('/me/password', { method: 'PUT', body: { currentPassword, newPassword } }),
  changeEmail: (newEmail, currentPassword) =>
    apiFetch('/me/email', { method: 'PUT', body: { newEmail, currentPassword } }),
  // Passkeys (WebAuthn): sign-in is anonymous, management requires a session.
  passkeyLoginOptions: () => apiFetch('/auth/passkey/options', { method: 'POST', auth: false }),
  passkeyLogin: (sessionId, assertion) =>
    apiFetch('/auth/passkey/login', { method: 'POST', body: { sessionId, assertion }, auth: false }),
  listPasskeys: () => apiFetch('/me/passkeys'),
  passkeyRegisterOptions: () => apiFetch('/me/passkeys/options', { method: 'POST' }),
  registerPasskey: (sessionId, name, attestation) =>
    apiFetch('/me/passkeys', { method: 'POST', body: { sessionId, name, attestation } }),
  deletePasskey: (passkeyId) => apiFetch(`/me/passkeys/${passkeyId}`, { method: 'DELETE' }),
  // Devices & sessions. The refresh token identifies "this device" server-side.
  listSessions: () =>
    apiFetch('/me/sessions/list', { method: 'POST', body: { refreshToken: getStored()?.refreshToken } }),
  revokeSession: (sessionId) => apiFetch(`/me/sessions/${sessionId}`, { method: 'DELETE' }),
  revokeOtherSessions: () =>
    apiFetch('/me/sessions/revoke-others', { method: 'POST', body: { refreshToken: getStored()?.refreshToken } }),
};
